import { z } from "zod";
import { email, password, phone, pin, trimmed, uuid } from "./common";
import { roleLimitsSchema, type RoleLimits } from "./approvals";
import { emailCode } from "./auth";

/** Назначение роли: branchId = null — роль scope=company на все филиалы. */
export const roleAssignment = z.strictObject({ roleId: uuid, branchId: uuid.nullable() });

export const userRolesSchema = z.strictObject({
  roles: z.array(roleAssignment).min(1, { error: "Назначьте хотя бы одну роль" }).max(50),
});

const contact = { email: email.optional(), phone: phone.optional() };

export const userCreateSchema = z
  .strictObject({
    name: trimmed(2, 120),
    ...contact,
    /** Пароль — для входа в ERP (учитывается в limit.users). Без пароля — только PIN на терминале. */
    password: password.optional(),
    pin: pin.optional(),
    roles: userRolesSchema.shape.roles,
    /** Код из письма на email сотрудника (`POST /users/email-code`) — обязателен, если email указан */
    emailCode: emailCode.optional(),
  })
  .refine((v) => v.password === undefined || v.email || v.phone, { error: "Для входа по паролю нужен email или телефон", path: ["email"] })
  .refine((v) => v.password || v.pin, { error: "Задайте пароль или PIN", path: ["pin"] });
export type UserCreateInput = z.input<typeof userCreateSchema>;

export const userUpdateSchema = z.strictObject({
  name: trimmed(2, 120).optional(),
  email: email.nullable().optional(),
  phone: phone.nullable().optional(),
  locale: z.enum(["ru", "ky", "en", "uz"]).optional(),
  /** Код из письма на новый email (`POST /users/email-code`) — обязателен при смене email */
  emailCode: emailCode.optional(),
});
export type UserUpdateInput = z.input<typeof userUpdateSchema>;

/** Код подтверждения на email сотрудника (добавление или смена email); `userId` — при смене, чтобы не считать его email занятым. */
export const userEmailCodeSchema = z.strictObject({ email, userId: uuid.optional() });

export const userSetPasswordSchema = z.strictObject({ password });
export const userSetPinSchema = z.strictObject({ pin });

export const usersQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().max(100).optional(),
  status: z.enum(["active", "blocked", "invited"]).optional(),
  branchId: uuid.optional(),
});

export type UserListItem = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: "active" | "blocked" | "invited";
  hasPassword: boolean;
  hasPin: boolean;
  lastLoginAt: string | null;
  roles: { roleId: string; code: string; name: string; branchId: string | null }[];
};

export const roleCreateSchema = z.strictObject({
  name: trimmed(2, 80),
  description: trimmed(0, 300).optional(),
  scope: z.enum(["company", "branch"]),
  permissions: z.array(z.string().regex(/^[a-z_]+\.[a-z_]+$/)).max(500),
  /** Лимиты без подтверждения менеджера (ТЗ 04 §2.1): скидка %, возврат/списание/изъятие — minor units */
  limits: roleLimitsSchema.optional(),
});
export const roleUpdateSchema = roleCreateSchema.omit({ scope: true }).partial();
export type RoleCreateInput = z.input<typeof roleCreateSchema>;

export type RoleItem = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  scope: "company" | "branch";
  isSystem: boolean;
  permissions: string[];
  limits: RoleLimits;
  usersCount: number;
};
